(ns arthur.export
  "Export a TIMELINE: one frame walk, and a sink that decides what comes out.

  THE SINK IS A PROTOCOL because there is more than one right answer to \"a
  video file\" and they disagree about the thing this project cares most about.
  A PNG sequence is bit-exact — the file holds the bytes `raster/draw-ops!`
  produced, expanded through the ramp and nothing else. A muxed MP4 is one file
  that plays anywhere, and every codec a browser can reach either subsamples
  chroma (which puts fringes on precisely the hard flat-colour edges the whole
  idiom is made of) or is a codec an NLE will not open. Those are different
  trades for different jobs, not a better and a worse, so both should be
  reachable and neither should be the other's special case.

  What is genuinely shared is everything above the sink, and it is most of the
  work: rooting the resolver at the chosen symbol, generated-channel picture
  sampling,
  the raster, the frame loop, the audio mix, the progress reporting and the
  yielding that lets the page paint. So `run!` owns all of that and calls three
  methods.

  TWO RULES THE WALK ENFORCES, both about sync:

  Every frame of the symbol's frame space is emitted, at the CLIP's rate. A
  lower picture rate holds a pose across several frames — it never drops them —
  so the exported duration matches the audio no matter what the picture rate is.
  Decimating instead is how an export silently runs short and the sound slides
  off the picture, which is the one artefact this tool exists to prevent.

  The zoom is an INTEGER. A pixel becomes a block of identical pixels. Anything
  else resamples, and `domain/png` and `ui/canvas` both have the longer argument
  for why that is not allowed to happen here."
  ;; `run!` is the verb this namespace is about, and nothing here folds a
  ;; side-effect over a seq, so core's loses the name rather than ours.
  (:refer-clojure :exclude [run!])
  (:require [arthur.audio.mix :as mix]
            [arthur.domain.clip :as clip]
            [arthur.domain.palette :as pal]
            [arthur.domain.raster :as raster]))

(defprotocol Exporter
  "A sink for a rendered symbol. Implementations live under `arthur.export.*`.

  Called in this order, once, per export: `begin!`, then `frame!` for every frame
  in order from 0, then `finish!`. Any of them may return a promise and the walk
  waits for it, which is what keeps a slow encoder from being fed faster than it
  drains and what gives the page a chance to paint between frames.

  `Exporter` rather than `IExporter`, which is what `domain/symbol`'s
  `IResolver` would suggest, because it names a role a thing plays rather than a
  capability a value has."

  (begin! [this spec]
    "Prepare to receive frames.

    `spec` carries everything constant for the export:

      :name        a filesystem-safe stem for the artefact
      :width       stage width in raster pixels, before zoom
      :height      stage height, before zoom
      :zoom        integer pixel multiplier
      :fps         frames per second of the finished file — the CLIP's rate
      :frames      how many frames will arrive
      :ramp        index -> [r g b], the palette to expand through
      :audio       an AudioBuffer, or nil when the symbol has no sound

    The ramp and the audio are here rather than on `frame!` because neither
    changes across an export, and a muxer has to declare its tracks before it
    will accept a sample.")

  (frame! [this i raster]
    "Take frame `i`, an indexed `domain/raster`.

    THE RASTER IS REUSED and must be consumed before this returns (or before the
    promise it returns settles). The walk hands back the same buffer every frame,
    for the same reason `symbol/resolver` reuses its point buffers: a 900-frame
    export that allocates a stage per frame is a tab that swaps. A sink that wants
    to keep pixels has to copy or encode them here.")

  (finish! [this]
    "Close the artefact. Promise of `{:filename :blob}`."))

(defn kin
  "The ids to keep when isolating `id` in `nodes`.

  Four things, and each for its own reason:

    the node itself;
    everything ABOVE it, because an instance's transform is relative to its
      parent and dropping the chain would move the thing being isolated;
    everything BELOW it, because a group instance is its children;
    any audio track `:linked-to` it, because the link is the statement that this
      sound belongs to that instance, and a face exported without its voice is
      not the thing that was asked for.

  Siblings go. That is the whole point: what comes out is one instance, where it
  sits, in the symbol it sits in."
  [nodes id]
  (let [up   (loop [i id acc #{}]
               (if (or (nil? i) (contains? acc i))
                 acc
                 (recur (:parent (get nodes i)) (conj acc i))))
        down (loop [edge #{id} acc #{}]
               (if (empty? edge)
                 acc
                 (let [acc' (into acc edge)]
                   (recur (set (for [[k n] nodes
                                     :when (and (contains? edge (:parent n))
                                                (not (contains? acc' k)))]
                                 k))
                          acc'))))
        kept (into up down)]
    (into kept
          (for [[k n] nodes
                :when (and (= :audio (:kind n)) (contains? kept (:linked-to n)))]
            k))))

(defn isolate
  "The symbol with only `id` and its kin kept. `nil` leaves it alone.

  The FRAME SPACE IS UNTOUCHED, which is what makes this different from exporting
  the symbol an instance plays. Rooting at `:sym/face-8625` renders the drawing in
  its own time, identically for all seven instances. Isolating one instance
  renders the STAGE — its length, its rate, the instance's span, drift and scale
  — with the other six removed. The first is the drawing; the second is that face
  on the stage, and they are different deliverables."
  [sym id]
  (if (and id (get-in sym [:nodes id]))
    (update sym :nodes select-keys (kin (:nodes sym) id))
    sym))

(defn- yield!
  "Hand the event loop a turn between frames.

  `setTimeout 0` and not a resolved promise: a promise continuation is a
  microtask, so a chain of them runs to completion without the browser ever
  painting, and the progress readout would jump from 0 to done. `flow/ingest`'s
  decode loop pauses for the same reason."
  []
  (js/Promise. (fn [resolve] (js/setTimeout resolve 0))))

(defn audio!
  "Promise of the AudioBuffer to export alongside the picture, or nil.

  A symbol's own placed audio tracks win. Failing that, the symbol the document
  OPENS ON — and only that one — falls back to the clip's audio file, which is
  where a take's sound lives before anyone has placed a track. Any other symbol
  exports silence rather than the whole clip's soundtrack, because its frame
  space is its own and the clip's audio is not a fact about it."
  [clip-doc sid store fallback-url]
  (-> (mix/buffer! clip-doc sid store)
      (.then (fn [buffer]
               (cond
                 buffer buffer
                 (and (= sid (clip/opens-on clip-doc)) fallback-url) (mix/decode! fallback-url)
                 :else nil)))))

(defn plan
  "What an export of `sid` will produce, without producing any of it.

  Separate from `run!` so the UI can show the size and length it is about to
  commit to, and so the arithmetic is assertable without a sink."
  [{:keys [clip zoom] sid :symbol isolate-id :isolate}]
  (let [sym   (some-> (clip/symbol clip sid) (isolate isolate-id))
        [width height] (clip/stage clip sid)
        zoom (max 1 (js/Math.floor (or zoom 1)))]
    (when sym
      {:frames  (clip/frames clip sid)
       :fps     (:fps clip)
       :zoom    zoom
       :width   (* width zoom)
       :height  (* height zoom)
       :seconds (/ (clip/frames clip sid) (:fps clip))})))

(defn run!
  "Render symbol `sid` into `exporter`. Promise of `{:filename :blob}`.

  `on-progress` is called with `[done total]` as frames complete, and is where a
  UI hangs its readout."
  [{:keys [clip store palette ramp zoom name audio-url]
    sid :symbol isolate-id :isolate}
   exporter on-progress]
  (let [sym (some-> (clip/symbol clip sid) (isolate isolate-id))]
    (when-not sym
      (throw (ex-info "there is no such symbol to export"
                      {:symbol sid
                       :symbols (vec (sort-by str (keys (:symbols clip))))})))
    (let [{:keys [frames fps zoom]} (plan {:clip clip :symbol sid :zoom zoom
                                           :isolate isolate-id})
          [width height] (clip/stage clip sid)
          ;; Rooted at the chosen symbol, as the stage is. Nested instances
          ;; inside it still resolve — clip/resolver is the function that knows
          ;; how.
          doc      (assoc-in clip [:symbols sid] sym)
          resolve-frame (clip/resolver doc sid store palette nil)
          ras      (raster/make width height)]
      (-> (audio! doc sid store audio-url)
          (.then (fn [audio]
                   (js/Promise.resolve
                    (begin! exporter {:name name :width width
                                      :height height :zoom zoom
                                      :fps fps :frames frames :ramp ramp
                                      :audio audio}))))
          (.then (fn [_]
                   ;; A fold over the frames as a promise CHAIN rather than a
                   ;; doseq: each frame has to wait for the last one's sink to
                   ;; drain, and `reduce` building that chain is the shape that
                   ;; says so. Nothing here is concurrent on purpose — an encoder
                   ;; fed from two places at once is not fast, it is wrong.
                   (reduce
                    (fn [chain i]
                      (.then chain
                             (fn [_]
                               (let [ops (resolve-frame i)
                                     active (clip/active-palette resolve-frame)]
                                 (-> ras
                                     (raster/clear! pal/bg-slot)
                                     (raster/draw-ops! ops))
                                 (-> (js/Promise.resolve
                                      (frame! exporter i
                                              (assoc ras :palette-ramp
                                                     (pal/effective-ramp palette active))))
                                   (.then (fn [_]
                                            (when on-progress
                                              (on-progress (inc i) frames))
                                            (yield!))))))))
                    (js/Promise.resolve)
                    (range frames))))
          (.then (fn [_] (finish! exporter)))))))
